import { motion } from 'framer-motion';
import { Music4, AudioWaveform, Cpu } from 'lucide-react';
import type { ReactNode } from 'react';

// ─── Skill Categories ──────────────────────────────────────────────────────────
interface SkillCategory {
  label: string;
  items: string[];
  accent: string;
}

const SKILLS: SkillCategory[] = [
  {
    label: 'Music & Production',
    items: ['Scrub master', 'Music briefs', 'Cue management', 'Asset delivery', 'Scheduling', 'Documentation', 'Collaborative workflows'],
    accent: 'var(--primary)',
  },
  {
    label: 'DAWs',
    items: ['Pro Tools', 'Cubase', 'Logic Pro', 'Ableton Live'],
    accent: 'var(--accent)',
  },
  {
    label: 'Music Libraries & Tools',
    items: ['Spitfire Audio', 'SINE Player', 'Kontakt', 'Vienna Synchron Player', 'CueDB'],
    accent: 'var(--chart-3)',
  },
  {
    label: 'Game Audio Middleware',
    items: ['FMOD', 'Wwise'],
    accent: 'var(--chart-4)',
  },
  {
    label: 'Notation',
    items: ['Sibelius', 'Dorico', 'Finale'],
    accent: 'var(--chart-5)',
  },
  {
    label: 'Software & Systems',
    items: ['Google Workspace', 'Microsoft Office'],
    accent: 'var(--primary)',
  },
  {
    label: 'Instruments',
    items: ['Piano', 'Guitar', 'Saxophone'],
    accent: 'var(--accent)',
  },
];

interface ExperienceEntry {
  title: string;
  role?: string;
  period: string;
  bullets: string[];
  highlight?: string; // e.g. "Semi-Finalist"
}

const COMPOSITION: ExperienceEntry[] = [
  {
    title: 'Clover',
    period: 'February 2026',
    bullets: [
      'Composing and producing around 10 minutes of music for an original score.',
      'Support spotting sessions, manage versioning, and ensure music stays aligned with editorial updates.',
    ],
  },
  {
    title: 'At the Catch',
    period: 'October 2025 – January 2026',
    bullets: [
      'Composing and delivering approximately 15 minutes of original score.',
      'Assist with spotting, version management, and alignment of music to editorial changes.',
    ],
  },
  {
    title: 'KAMAZ',
    period: 'December 2025',
    bullets: [
      'Composed and delivered around 2 minutes of music for a promotional video campaign for Kamaz, a major European truck manufacturer.',
      'Successfully overcame stiff deadlines, with new drafts being required within the next 24 hours.',
    ],
  },
  {
    title: 'RedBull Scoring Challenge',
    role: 'Semi-Finalist',
    period: 'December 2024',
    highlight: 'Semi-Finalist — Vienna, Austria',
    bullets: [
      'Participated in an annual RedBull music competition, where participants had to enhance the ethos and spirit of RedBull.',
      'Selected among 200 applicants for an international music competition.',
      'Delivered music aligned to creative briefs, brand identity, and visual media.',
    ],
  },
  {
    title: "A Midsummer Night's Dream",
    period: 'June 2021',
    bullets: [
      'Composed approximately 25 cues for a full stage production.',
      'Managed cue timing, revisions, and coordination with production staff.',
    ],
  },
];

const SOUND_DESIGN: ExperienceEntry[] = [
  {
    title: 'Noodle',
    period: 'March 2026 – Present',
    bullets: [
      'Designing and delivering a total of 30 assets for the fictional arcade game Noodle.',
      'Working and delivering assets in a timely manner with punctuality.',
      'Working within a specified time frame and following the burn-down chart.',
    ],
  },
  {
    title: 'Neon Frontier',
    period: 'March – April 2023',
    bullets: [
      'Designed and delivered 12 sound design assets for the sci-fi shooter Neon Frontier.',
      'Coordinated with composers and project managers to meet production timelines.',
      'Managed asset organization, naming conventions, and delivery structure.',
    ],
  },
  {
    title: 'FosterKent Studios',
    role: 'Intern',
    period: 'May 2022 – June 2022',
    bullets: [
      'Assisted in organization, production, and operation of the Gala event for the company.',
      'Assisted with recording sessions, studio scheduling, and session preparation.',
      'Organized physical and digital music assets, session files, and documentation.',
      'Supported logistics and operations for a company gala event.',
    ],
  },
];

interface EntryCardProps {
  entry: ExperienceEntry;
  index: number;
  accentVar: string;
}

function EntryCard({ entry, index, accentVar }: EntryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="relative pl-6 group"
    >
      {/* Timeline vertical line connector */}
      <div
        className="absolute left-0 top-0 bottom-0 w-px"
        style={{ background: `linear-gradient(to bottom, ${accentVar}, transparent)` }}
      />
      {/* Timeline dot */}
      <div
        className="absolute left-[-4px] top-2 w-2 h-2 rounded-full border-2"
        style={{ borderColor: accentVar, background: 'var(--background)' }}
      />

      <div
        className="rounded-2xl border border-border bg-card/50 backdrop-blur-sm p-5 transition-all duration-200 group-hover:border-opacity-80"
        style={{
          boxShadow: `0 4px 24px -8px ${accentVar}33`,
        }}
      >
        {/* Header row */}
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div>
            {entry.highlight && (
              <span
                className="inline-block text-[10px] font-mono font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full mb-1"
                style={{
                  background: `${accentVar}22`,
                  color: accentVar,
                  border: `1px solid ${accentVar}44`,
                }}
              >
                {entry.highlight}
              </span>
            )}
            <h4 className="font-bold text-base text-foreground leading-tight">
              {entry.role && (
                <span className="text-sm font-medium" style={{ color: accentVar }}>
                  {entry.role},{' '}
                </span>
              )}
              {entry.title}
            </h4>
          </div>
          <span
            className="text-xs font-mono shrink-0 mt-0.5"
            style={{ color: 'var(--muted-foreground)' }}
          >
            {entry.period}
          </span>
        </div>

        {/* Bullets */}
        <ul className="space-y-1.5">
          {entry.bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
              <span className="mt-[5px] w-1 h-1 rounded-full flex-shrink-0" style={{ background: accentVar }} />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

interface ColumnProps {
  title: string;
  icon: ReactNode;
  entries: ExperienceEntry[];
  accentVar: string;
  accentLabel: string;
}

function ExperienceColumn({ title, icon, entries, accentVar, accentLabel }: ColumnProps) {
  return (
    <div className="flex flex-col gap-6">
      {/* Column header */}
      <motion.div
        initial={{ opacity: 0, x: -16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="flex items-center gap-3"
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accentVar}22`, color: accentVar }}
        >
          {icon}
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accentVar }}>
            {accentLabel}
          </p>
          <h3 className="font-bold text-xl text-foreground">{title}</h3>
        </div>
      </motion.div>

      {/* Entries */}
      <div className="flex flex-col gap-4">
        {entries.map((entry, i) => (
          <EntryCard key={entry.title} entry={entry} index={i} accentVar={accentVar} />
        ))}
      </div>
    </div>
  );
}

export function ExperienceSection() {
  return (
    <section id="experience" className="py-24 px-6 relative">
      {/* Section divider glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px"
        style={{
          background:
            'linear-gradient(to right, transparent, var(--primary), var(--accent), transparent)',
        }}
      />

      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs font-mono text-primary uppercase tracking-widest mb-3">
            Background
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Experience</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            A growing track record across film, stage, advertising, and interactive media — built on
            craft, communication, and meeting deadlines.
          </p>
        </motion.div>

        {/* Two-column grid */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          <ExperienceColumn
            title="Composition"
            icon={<Music4 className="w-5 h-5" />}
            entries={COMPOSITION}
            accentVar="var(--primary)"
            accentLabel="Composition Experience"
          />
          <ExperienceColumn
            title="Sound Design"
            icon={<AudioWaveform className="w-5 h-5" />}
            entries={SOUND_DESIGN}
            accentVar="var(--accent)"
            accentLabel="Sound Design Experience"
          />
        </div>

        {/* Skills Block */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
        >
          {/* Skills header */}
          <div className="flex items-center gap-3 mb-10">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--chart-4)', color: 'var(--background)' }}
            >
              <Cpu className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: 'var(--chart-4)' }}>
                Toolkit
              </p>
              <h3 className="font-bold text-xl text-foreground">Skills & Software</h3>
            </div>
          </div>

          {/* Skill category grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {SKILLS.map((cat, ci) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.4, delay: ci * 0.07 }}
                className="rounded-2xl border border-border bg-card/50 backdrop-blur-sm p-5"
                style={{
                  borderTopColor: cat.accent,
                  borderTopWidth: '2px',
                  boxShadow: `0 6px 24px -8px ${cat.accent}28`,
                }}
              >
                <p
                  className="text-[10px] font-mono uppercase tracking-widest mb-3 font-semibold"
                  style={{ color: cat.accent }}
                >
                  {cat.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {cat.items.map(item => (
                    <span
                      key={item}
                      className="text-xs font-medium px-2.5 py-1 rounded-lg"
                      style={{
                        background: `${cat.accent}18`,
                        color: 'var(--foreground)',
                        border: `1px solid ${cat.accent}30`,
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
